import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { shortId } from './auditFormat';

export interface ActorChipProps {
  /** Actor role as stored: ADMIN, SUPER_ADMIN, GARAGE_OWNER/MANAGER/STAFF, CUSTOMER, SYSTEM. */
  role?: string | null;
  /** Resolved display name; null when the resolver could not name the actor. */
  name?: string | null;
  /** Keycloak subject id — shown only as the last 8 characters, never in full. */
  subjectId?: string | null;
  /**
   * A server-built label such as "ADMIN · Maher Asha" or "GARAGE OWNER · Ali (Speedy Garage)"
   * (order timeline `actorLabel`). Wins over role + name when present.
   */
  label?: string | null;
  /** Appended in brackets after the name, e.g. the garage of a garage member. */
  suffix?: string | null;
  /** The actor no longer exists (AC-B7): the snapshot name is kept and marked. */
  removed?: boolean;
  /** Link admins and customers to their user page when the viewer holds `users`. Default true. */
  link?: boolean;
}

type RoleKind = 'admin' | 'garage' | 'customer' | 'system' | 'other';

function roleKind(role: string | null | undefined): RoleKind {
  const r = (role ?? '').toUpperCase();
  if (r === 'ADMIN' || r === 'SUPER_ADMIN' || r.startsWith('ADMIN') || r.includes('GARAGAT ADMIN')) return 'admin';
  if (r.startsWith('GARAGE')) return 'garage';
  if (r === 'CUSTOMER') return 'customer';
  if (r === 'SYSTEM') return 'system';
  return 'other';
}

const PILL: Record<RoleKind, string> = {
  admin: 'Admin', garage: 'Garage', customer: 'Customer', system: 'System', other: '',
};

/** The name-less fallbacks of design §2d. */
const FALLBACK: Record<RoleKind, string> = {
  admin: 'Admin', garage: 'Garage user', customer: 'Customer', system: 'System', other: 'Unknown',
};

/**
 * Spec 069 — the one way the admin portal shows who did something: a role pill and a name, never a
 * bare subject id. Without a name an admin reads "Admin · <last 8>", others read "Garage user",
 * "Customer" or "System". The full id is never rendered; the tooltip carries its last 8 characters.
 */
export default function ActorChip({ role, name, subjectId, label, suffix, removed, link = true }: ActorChipProps) {
  const { hasScreen } = useAuth();
  const tail = shortId(subjectId);
  const title = tail ? `…${tail}` : undefined;

  let pillText: string;
  let nameText: string | null;
  let kind: RoleKind;
  let mono = false;

  const trimmedLabel = label?.trim();
  if (trimmedLabel) {
    const sep = trimmedLabel.indexOf(' · ');
    if (sep > 0) {
      pillText = trimmedLabel.slice(0, sep);
      nameText = trimmedLabel.slice(sep + 3);
    } else {
      pillText = '';
      nameText = trimmedLabel;
    }
    kind = roleKind(role ?? pillText);
  } else {
    kind = roleKind(role);
    const trimmedName = name?.trim();
    if (trimmedName) {
      pillText = PILL[kind];
      nameText = suffix ? `${trimmedName} (${suffix})` : trimmedName;
    } else if (kind === 'admin' && tail) {
      pillText = 'Admin';
      nameText = tail;
      mono = true;
    } else {
      pillText = '';
      nameText = FALLBACK[kind];
    }
  }

  const content = (
    <>
      {pillText && <span className={`actor-chip__role actor-chip__role--${kind}`}>{pillText}</span>}
      {pillText && nameText && <span className="actor-chip__sep" aria-hidden="true"> · </span>}
      {nameText && <span className={`actor-chip__name${mono ? ' actor-chip__name--mono' : ''}`}>{nameText}</span>}
      {removed && <span className="actor-chip__removed"> (removed)</span>}
    </>
  );

  const linkable = link && !!subjectId && (kind === 'admin' || kind === 'customer') && hasScreen('users');
  if (linkable) {
    return (
      <Link
        to={`/users/${subjectId}`}
        className="actor-chip actor-chip--link"
        title={title}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </Link>
    );
  }
  return <span className="actor-chip" title={title}>{content}</span>;
}
